import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { toast } from "@/lib/toast";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AnnouncementDetailDialog } from "@/components/announcements/AnnouncementDetailDialog";
import {
  useAudiencePreview,
  useCreateAnnouncement,
  useUpdateAnnouncement,
  type AnnouncementAdminResponse,
  type AnnouncementCreateBody,
} from "@/hooks/useAdminAnnouncements";

const VALID_TAGS = ["internal", "beta", "friend", "do_not_email"] as const;
type TagId = (typeof VALID_TAGS)[number];

const TITLE_MAX = 120;
const BODY_MAX = 2000;
const CTA_LABEL_MAX = 40;

export type Mode =
  | "new"
  | { kind: "new" }
  | { kind: "edit"; announcement: AnnouncementAdminResponse };

interface Props {
  mode: Mode;
  open: boolean;
  onClose: () => void;
}

function normalizeMode(
  mode: Mode,
): { kind: "new" } | { kind: "edit"; announcement: AnnouncementAdminResponse } {
  if (mode === "new") return { kind: "new" };
  return mode;
}

function counterClass(len: number, max: number): string {
  const pct = (len / max) * 100;
  if (pct >= 100) return "text-destructive";
  if (pct >= 80) return "text-amber-500";
  return "text-muted-foreground";
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export function AnnouncementFormDrawer({
  mode: rawMode,
  open,
  onClose,
}: Props) {
  const mode = normalizeMode(rawMode);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [targetTags, setTargetTags] = useState<TagId[]>([]);
  const [allUsers, setAllUsers] = useState(false);
  const [emailRequested, setEmailRequested] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Hydrate when editing; reset when opening a new-mode form.
  useEffect(() => {
    if (!open) return;
    if (mode.kind === "edit") {
      const a = mode.announcement;
      setTitle(a.title);
      setBody(a.body_md);
      setCtaLabel(a.cta_label ?? "");
      setCtaUrl(a.cta_url ?? "");
      setTargetTags(a.target_tags as TagId[]);
      setAllUsers(a.all_users);
      setEmailRequested(a.email_requested);
    } else {
      setTitle("");
      setBody("");
      setCtaLabel("");
      setCtaUrl("");
      setTargetTags([]);
      setAllUsers(false);
      setEmailRequested(false);
    }
    // rawMode (the original prop reference) is the stable dep; normalizeMode
    // returns a fresh object each render for the string form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rawMode]);

  // --- Validation derivations ---
  const ctaPairValid =
    (ctaLabel.trim() === "" && ctaUrl.trim() === "") ||
    (ctaLabel.trim() !== "" && ctaUrl.trim() !== "");

  const ctaUrlSchemeValid =
    ctaUrl.trim() === "" ||
    ctaUrl.startsWith("/") ||
    ctaUrl.startsWith("https://");

  const audienceValid = allUsers || targetTags.length > 0;

  const titleValid = title.length > 0 && title.length <= TITLE_MAX;
  const bodyValid = body.length > 0 && body.length <= BODY_MAX;
  const ctaLabelValid = ctaLabel.length <= CTA_LABEL_MAX;

  const isValid =
    titleValid &&
    bodyValid &&
    ctaLabelValid &&
    ctaPairValid &&
    ctaUrlSchemeValid &&
    audienceValid;

  // --- Audience preview + email-checkbox derived state ---
  const audience = useAudiencePreview(targetTags, allUsers, audienceValid);

  const emailAlreadySent =
    mode.kind === "edit" && mode.announcement.email_sent_at !== null;

  // --- Mutations ---
  const create = useCreateAnnouncement();
  const update = useUpdateAnnouncement();

  async function handlePublish() {
    if (!isValid) return;
    if (mode.kind === "new" && allUsers && emailRequested) {
      setConfirmOpen(true);
      return;
    }
    await actuallySubmit();
  }

  async function actuallySubmit() {
    setConfirmOpen(false);
    const body_: AnnouncementCreateBody = {
      title,
      body_md: body,
      cta_label: ctaLabel.trim() || null,
      cta_url: ctaUrl.trim() || null,
      target_tags: allUsers ? [] : targetTags,
      all_users: allUsers,
      email_requested: emailRequested,
    };
    try {
      if (mode.kind === "new") {
        const res = await create.mutateAsync(body_);
        if (res.email_status === "skipped_resend_not_configured") {
          toast.warning(
            "Announcement published. Email skipped — RESEND_API_KEY not configured.",
          );
        } else if (res.email_status === "scheduled") {
          toast.success(
            "Announcement published — email sending in background.",
          );
        } else {
          toast.success("Announcement published.");
        }
      } else {
        const { email_requested: _unused, ...patchable } = body_;
        await update.mutateAsync({
          id: mode.announcement.id,
          body: patchable,
        });
        toast.success("Announcement updated.");
      }
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Save failed");
    }
  }

  const announcementForPreview = useMemo(
    () => ({
      id: "preview",
      title,
      body_md: body,
      cta_label: ctaLabel.trim() || null,
      cta_url: ctaUrl.trim() || null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }),
    [title, body, ctaLabel, ctaUrl],
  );

  function toggleTag(tag: TagId) {
    setTargetTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  }

  if (!open) return null;

  const headingLabel =
    mode.kind === "new" ? "New announcement" : "Edit announcement";

  const ctaUrlSchemeError =
    ctaUrl.trim() !== "" &&
    !ctaUrl.startsWith("/") &&
    !ctaUrl.startsWith("https://");

  const ctaPairError = !ctaPairValid;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-[32rem] max-w-full bg-background border-l shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-semibold text-base">{headingLabel}</h2>
          <button
            onClick={onClose}
            type="button"
            className="rounded-md p-1 hover:bg-muted transition-colors"
            aria-label="Close drawer"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Body (scrollable) */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Title */}
          <div className="space-y-1.5">
            <label
              htmlFor="announcement-title"
              className="text-sm font-medium"
            >
              Title
            </label>
            <Input
              id="announcement-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Announcement title"
              maxLength={TITLE_MAX + 200 /* allow over-typing for red state */}
            />
            <div
              className={
                "text-xs text-right " + counterClass(title.length, TITLE_MAX)
              }
            >
              {title.length}/{TITLE_MAX}
            </div>
          </div>

          {/* Body */}
          <div className="space-y-1.5">
            <label
              htmlFor="announcement-body"
              className="text-sm font-medium"
            >
              Body (Markdown)
            </label>
            <Textarea
              id="announcement-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Supports **markdown**. Use paragraphs, lists, links."
              className="min-h-32"
            />
            <div
              className={
                "text-xs text-right " + counterClass(body.length, BODY_MAX)
              }
            >
              {body.length}/{BODY_MAX}
            </div>
          </div>

          {/* CTA pair */}
          <div className="space-y-2 rounded-md border p-3">
            <div className="text-sm font-medium">Call to action (optional)</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label
                  htmlFor="announcement-cta-label"
                  className="text-xs text-muted-foreground"
                >
                  CTA label
                </label>
                <Input
                  id="announcement-cta-label"
                  value={ctaLabel}
                  onChange={(e) => setCtaLabel(e.target.value)}
                  placeholder="e.g. Learn more"
                  aria-invalid={!ctaLabelValid}
                />
                <div
                  className={
                    "text-xs text-right " +
                    counterClass(ctaLabel.length, CTA_LABEL_MAX)
                  }
                >
                  {ctaLabel.length}/{CTA_LABEL_MAX}
                </div>
              </div>
              <div className="space-y-1">
                <label
                  htmlFor="announcement-cta-url"
                  className="text-xs text-muted-foreground"
                >
                  CTA URL
                </label>
                <Input
                  id="announcement-cta-url"
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                  placeholder="/route or https://…"
                  aria-invalid={ctaUrlSchemeError}
                  className={
                    ctaUrlSchemeError
                      ? "border-destructive focus-visible:ring-destructive/30"
                      : ""
                  }
                />
                {ctaUrl.startsWith("/") && (
                  <div className="text-xs text-muted-foreground">
                    ↳ Internal route
                  </div>
                )}
                {ctaUrl.startsWith("https://") && (
                  <div className="text-xs text-muted-foreground">
                    ↗ External — opens in new tab
                  </div>
                )}
                {ctaUrlSchemeError && (
                  <div className="text-xs text-destructive">
                    Must start with <code>/</code> or <code>https://</code>
                  </div>
                )}
              </div>
            </div>
            {ctaPairError && (
              <div className="text-xs text-destructive">
                Both label and URL required (or leave both blank)
              </div>
            )}
          </div>

          {/* Audience */}
          <div className="space-y-2 rounded-md border p-3">
            <div className="text-sm font-medium">Audience</div>
            <div className="flex items-center gap-2">
              <input
                id="announcement-all-users"
                type="checkbox"
                checked={allUsers}
                onChange={(e) => setAllUsers(e.target.checked)}
                className="size-4 rounded border-input"
              />
              <label
                htmlFor="announcement-all-users"
                className="text-sm select-none"
              >
                All users
              </label>
            </div>
            <div
              className={
                "flex flex-wrap gap-2 pt-1 " +
                (allUsers ? "opacity-50 pointer-events-none" : "")
              }
              aria-disabled={allUsers}
            >
              {VALID_TAGS.map((tag) => {
                const active = targetTags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    title={
                      tag === "do_not_email"
                        ? "Marketing-suppression flag — does not affect in-app modal/bell."
                        : undefined
                    }
                    className={
                      "rounded-full border px-3 py-1 text-xs transition-colors " +
                      (active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-muted-foreground hover:bg-muted")
                    }
                    aria-pressed={active}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
            {!audienceValid && (
              <div className="text-xs text-muted-foreground">
                Select at least one tag or enable "All users".
              </div>
            )}
            {audienceValid && audience.data && (
              <div className="text-xs text-muted-foreground pt-1">
                Target: ~{audience.data.target_user_count} users
              </div>
            )}
          </div>

          {/* Email broadcast — only meaningful at publish time. PATCH
              never re-sends or changes email_requested, so in edit mode
              we either show the sent-status line or hide the section
              entirely. */}
          {mode.kind === "new" ? (
            <div className="space-y-2 rounded-md border p-3">
              <div className="flex items-center gap-2">
                <input
                  id="announcement-email-requested"
                  type="checkbox"
                  checked={emailRequested}
                  onChange={(e) => setEmailRequested(e.target.checked)}
                  className="size-4 rounded border-input"
                />
                <label
                  htmlFor="announcement-email-requested"
                  className="text-sm select-none"
                >
                  Also send as email
                </label>
              </div>
              {audienceValid && audience.data && (
                <div className="text-xs text-muted-foreground">
                  Will email ≈{audience.data.email_eligible_count} users
                  (excluding do_not_email)
                </div>
              )}
            </div>
          ) : emailAlreadySent ? (
            <div className="rounded-md border p-3 text-xs text-muted-foreground italic">
              Emailed at {formatDate(mode.announcement.email_sent_at)} —
              edits do not re-send. To re-email, delete and re-publish.
            </div>
          ) : (
            <div className="rounded-md border p-3 text-xs text-muted-foreground italic">
              Email broadcast is set at publish time only. To email this
              announcement now, delete and re-publish with the email box
              checked.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            variant="outline"
            type="button"
            onClick={() => setPreviewOpen(true)}
            disabled={!titleValid || !bodyValid}
          >
            Preview
          </Button>
          <Button
            type="button"
            onClick={handlePublish}
            disabled={!isValid || create.isPending || update.isPending}
          >
            {mode.kind === "edit" ? "Save" : "Publish"}
          </Button>
        </div>
      </div>

      {/* Preview dialog */}
      <AnnouncementDetailDialog
        announcement={announcementForPreview}
        open={previewOpen}
        previewMode
        onClose={() => setPreviewOpen(false)}
      />

      {/* Confirmation dialog (all_users + email) */}
      <Dialog
        open={confirmOpen}
        onOpenChange={(v) => !v && setConfirmOpen(false)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send email to all users?</DialogTitle>
            <DialogDescription>
              This will email ≈{audience.data?.email_eligible_count ?? "?"}{" "}
              users (excluding do_not_email). Confirm?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              type="button"
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" onClick={actuallySubmit}>
              Publish + email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
